import { useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { stripCredentialParams } from "../lib/stripCredentialParams";
import Nav from "./Nav";
import Footer from "./Footer";

/**
 * Gates a route behind authentication. While the global auth check is in
 * flight we show a quiet placeholder (no redirect storm). If the user is
 * not signed in we render a calm gate card with a Google sign-in button.
 *
 * The original destination is stashed in sessionStorage so AuthContext's
 * signIn() picks it up and routes the user back after Google returns.
 */
export default function RequireAuth({ children, label = "Sign in to continue" }) {
    const { user, loading, signIn } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div
                data-testid="require-auth-loading"
                className="min-h-screen bg-paper flex items-center justify-center"
            >
                <div className="text-ink-soft font-mono text-[11px] uppercase tracking-[0.2em]">
                    Loading
                </div>
            </div>
        );
    }

    if (!user) {
        const onSignIn = () => {
            try {
                sessionStorage.setItem(
                    "scope:post_login",
                    stripCredentialParams(location.pathname + location.search)
                );
            } catch (_) { /* ignore */ }
            signIn();
        };
        return (
            <div className="min-h-screen bg-paper">
                <Nav />
                <main className="pt-32 pb-24 px-6 sm:px-10">
                    <div
                        data-testid="auth-gate"
                        className="max-w-xl mx-auto bg-paper-soft/60 border border-ink/15 px-8 py-12 text-center"
                    >
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-accent mb-4">
                            Authentication required
                        </div>
                        <h2 className="font-serif text-3xl text-ink leading-tight mb-3">
                            {label}
                        </h2>
                        <p className="font-serif text-ink-soft leading-relaxed mb-8">
                            Analyses and chat are tied to your account so your
                            work stays yours.
                        </p>
                        <button
                            data-testid="gate-signin-button"
                            onClick={onSignIn}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-ink text-paper text-[13px] tracking-wide hover:bg-amber-accent transition-colors"
                        >
                            <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
                                <path fill="#fff" d="M44.5 20H24v8.5h11.8C34.7 33 30 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l6-6C34.5 5.8 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11.1 0 20-8.9 20-20 0-1.4-.1-2.7-.5-4z" />
                            </svg>
                            Sign in with Google
                        </button>
                        <p className="mt-6 text-[11px] font-mono uppercase tracking-[0.15em] text-ink-soft/70">
                            You&rsquo;ll come back to this page.
                        </p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return children;
}
